import { inject, Injectable } from "@angular/core";
import { Period, PlanDetails, StudyYear } from "@app/core/domain/plan-details";
import { ApiCursus } from "@core/api/model/api-cursus";
import { ApiPlanDetail, ApiPlanPeriode, ApiStudiejaar } from "@core/api/model/api-plan";
import { Course, PlanYearAndPeriod } from "@core/domain/course";
import { ApiHelper } from "@core/domain/helpers/api-helper";
import { CourseHelper } from "@core/domain/helpers/course-helper";
import { StatusMessageHelper } from "@core/domain/helpers/status-message-helper";
import { TranslateService } from "@ngx-translate/core";
import { ApiAfterSubmitProposalResponse, ApiDocumentToUpload } from "../api/model/api-indienen-voorstel";
import { ApiStatusmelding } from "../api/model/api-statusmelding";
import { ApiToets } from "../api/model/api-toets";
import { CourseTest } from "../domain/course-test";
import { PlanHelper } from "../domain/helpers/plan-helper";
import { StatusMessage, StatusMessageType } from "../domain/status-message";
import { AfterSubmitProposalResponse, DocumentToUpload } from "../domain/submit-proposal";
import { CaciUtil } from "../util/caci-util";
import { CourseMappingService } from "./course-mapping.service";

@Injectable({
  providedIn: "root",
})
export class PlanMappingService {
  private readonly courseHelper = inject(CourseHelper);
  private readonly courseMappingService = inject(CourseMappingService);
  private readonly messageHelper = inject(StatusMessageHelper);
  private readonly planHelper = inject(PlanHelper);
  private readonly translate = inject(TranslateService);

  private plan!: PlanDetails;

  private initPlanAllowsChangeExamComponent(apiPlan: ApiPlanDetail): boolean {
    return ApiHelper.caciBooleanToBoolean(apiPlan.plaats_in_onderdeel_via_planapp_toegestaan);
    // uitgesteld, zie OB-30864: && !ApiHelper.caciBooleanToBoolean(apiPlan.concept_planning)
  }

  mapPlanDetails(apiPlan: ApiPlanDetail, forValidation = false): PlanDetails {
    this.plan = {
      allowsTestPlanning: ApiHelper.caciBooleanToBoolean(apiPlan.inplannen_toetsen),
      allowsChangeExamComponent: this.initPlanAllowsChangeExamComponent(apiPlan),
      credits: apiPlan.studiepunten_behaald,
      creditsPlannedPast: CaciUtil.roundStudyPoints(
        (apiPlan.studiepunten_gepland_totaal ?? 0) - (apiPlan.studiepunten_gepland ?? 0), // W 28634
      ),
      creditsTotalObtained: apiPlan.studiepunten_behaald_totaal,
      creditsTotalPlanned: apiPlan.studiepunten_gepland_totaal,
      currentYear: apiPlan.huidige_jaar,
      degreeProgram: apiPlan.opleiding,
      electivesName: apiPlan.profil_naam,
      examStage: apiPlan.examenfase,
      examType: apiPlan.examentype,
      examTypeCode: apiPlan.examentype_code,
      graduationRequestStatus: apiPlan.student_diploma_aanvraag_status,
      hasSpecialisations: ApiHelper.caciBooleanToBoolean(apiPlan.heeft_specialisaties),
      id: apiPlan.spla_id,
      isDraft: ApiHelper.caciBooleanToBoolean(apiPlan.concept_planning),
      lastSaved: apiPlan.laatst_opgeslagen,
      name: apiPlan.planning_naam,
      pointsPlanned: apiPlan.studiepunten_gepland,
      proposalAdviceDate: apiPlan.datum_advies,
      proposalAdviceExplanation: apiPlan.toelichting_advies,
      proposalAdviceStatus: apiPlan.status_advies,
      proposalBlocksExamComponents: apiPlan.blokkeer_examenonderdelen,
      proposalDecisionDate: apiPlan.datum_besluit,
      proposalDecisionExplanation: apiPlan.toelichting_besluit,
      proposalDocumentToUpload: apiPlan.document_uploaden,
      proposalNoResubmitWithGraduationRequestStatuses: apiPlan.niet_opnieuw_indienen_bij_dipl_aanvr,
      proposalResubmitAllowedByStudyProgram: ApiHelper.caciBooleanToBoolean(apiPlan.opnieuw_indienen),
      proposalReviewMoment: apiPlan.beoordelingsmoment,
      proposalScope: apiPlan.indienen_betreft,
      proposalStatus: apiPlan.status,
      proposalStatusAdviceBy: apiPlan.status_advies_door,
      proposalStatusBy: apiPlan.status_door,
      proposalSubmitDate: apiPlan.datum_indienen,
      proposalSubmitMinPointsObtained: apiPlan.indienen_min_punten_behaald,
      proposalSubmitMinPointsPlanned: apiPlan.indienen_min_punten_gepland,
      scheduleMinor: ApiHelper.caciBooleanToBoolean(apiPlan.kan_minor_inplannen),
      secondExamStage: apiPlan.tweede_examenfase,
      secondExamTypeCode: apiPlan.tweede_examentype_code,
      secondStudyProgram: apiPlan.tweede_examenprogramma,
      secondStudyProgramObtained: ApiHelper.caciBooleanToBoolean(apiPlan.tweede_examenprogramma_behaald),
      staffMember: apiPlan.medewerker,
      startingYear: apiPlan.aanvangsjaar_student,
      statusMessages: undefined,
      studentName: apiPlan.studentnaam,
      studentNr: apiPlan.studentnummer,
      studyProgram: apiPlan.examenprogramma,
      studyProgramObtained: ApiHelper.caciBooleanToBoolean(apiPlan.examenprogramma_behaald),
      studyYears: this.mapStudyYears(apiPlan.studiejaren),
      validationAllowed: ApiHelper.caciBooleanToBoolean(apiPlan.is_validatie_toegestaan),
      yearCount: apiPlan.aantal_jaar,
      startYear: apiPlan.start_jaar,
    };

    this.extractStatusMessages(apiPlan, forValidation);

    this.plan.studyYears.forEach((year) => {
      this.enrichRibbonCourses(year);
      this.enrichCourses(this.plan.id, year);
      this.enrichWithTests(year);
    });

    return this.plan;
  }

  mapAfterSubmitProposalResponse(
    apiAfterSubmitProposalResponse: ApiAfterSubmitProposalResponse,
  ): AfterSubmitProposalResponse {
    return {
      planId: apiAfterSubmitProposalResponse.spla_id,
      studentNr: apiAfterSubmitProposalResponse.studentnummer,
      documentsToUpload: apiAfterSubmitProposalResponse.documenten?.map((doc) => this.mapDocumentToUpload(doc)) ?? [],
      statusMessages: apiAfterSubmitProposalResponse.statusmeldingen?.map((msg) => ApiHelper.toStatusMessage(msg)),
    };
  }

  mapDocumentToUpload(document: ApiDocumentToUpload): DocumentToUpload {
    return {
      documentName: document.bestandsnaam,
      documentSize: document.document_grootte,
      documentId: document.doc_id,
    };
  }

  private mapStudyYears(apiStudiejaren: ApiStudiejaar[]) {
    return apiStudiejaren?.filter((year) => year.periodes?.length)?.map((year) => this.mapStudyYear(year)) ?? [];
  }

  private mapStudyYear(apiStudiejaar: ApiStudiejaar): StudyYear {
    return {
      yearNr: apiStudiejaar.studiejaar,
      academicYear: apiStudiejaar.collegejaar,
      academicYearDescription: apiStudiejaar.collegejaar_oms,
      periods: apiStudiejaar.periodes.map((periode) => this.mapPeriod(periode, apiStudiejaar.studiejaar)),
    };
  }

  private mapPeriod(apiPeriode: ApiPlanPeriode, yearNr: number): Period {
    const periodNr = apiPeriode.periode_nr;
    const periodDescription = apiPeriode.periode_oms;
    return {
      periodNr: apiPeriode.periode_nr,
      periodDescription: apiPeriode.periode_oms,
      actual: ApiHelper.caciBooleanToBoolean(apiPeriode.actueel),
      courses: apiPeriode.cursussen.map((cursus) => {
        return this.mapCourse(cursus, yearNr, periodNr, periodDescription);
      }),
      // verwijder als oude toetsinschrijvingen verwijderd worden
      courseTestsOld:
        apiPeriode.toetsinschrijvingen?.map((toets) => this.mapTest(toets, yearNr, periodNr, periodDescription)) ?? [],
      courseTests: apiPeriode.toetsen?.map((toets) => this.mapTest(toets, yearNr, periodNr, periodDescription)) ?? [],
    };
  }

  public mapCourse(apiCursus: ApiCursus, yearNr = 0, periodNr = 0, periodDescr = ""): Course {
    const course = this.courseMappingService.mapCourse(apiCursus);
    course.yearNr = yearNr; // If set, the course has been planned
    course.periodNr = periodNr;
    course.periodDescription = periodDescr;
    return course;
  }

  protected mapTest(toets: ApiToets, yearNr = 0, periodNr = 0, periodDescr = ""): CourseTest {
    const enrolledPeriods = this.mapTestEnrolledPeriods(toets);
    return {
      courseCode: toets.cursus,
      courseName: toets.cursus_korte_naam,
      testCode: toets.toets,
      testDescription: toets.toets_omschrijving,
      statusObtained: ApiHelper.caciBooleanToBooleanOrUndefined(toets.status_behaald),
      isEnrolled: enrolledPeriods.length > 0,
      enrolledPeriods: enrolledPeriods,
      // transient enriched fields:
      yearNr: yearNr,
      periodNr: periodNr,
      periodDescription: periodDescr,
      isTest: true,
    };
  }

  protected mapTestEnrolledPeriods(apiToets: ApiToets): PlanYearAndPeriod[] {
    if (!ApiHelper.caciBooleanToBoolean(apiToets.status_ingeschreven)) {
      if (apiToets.ingeschreven_periodes) {
        return apiToets.ingeschreven_periodes.map((enrolledPeriod) => ({
          yearNr: enrolledPeriod.studiejaar,
          periodNr: enrolledPeriod.periode_nr,
        }));
      }
      return [];
    }
    // If enrolled, there is always a status study year and period
    return [
      {
        yearNr: apiToets.status_studiejaar!,
        periodNr: apiToets.status_periode!,
      },
    ];
  }

  private readonly sortApiStatusmeldingenOnColumn = (
    statusmeldingA: ApiStatusmelding,
    statusmeldingB: ApiStatusmelding,
  ): number => {
    return statusmeldingA.kolom.localeCompare(statusmeldingB.kolom);
  };

  private extractStatusMessages(apiPlan: ApiPlanDetail, forValidation = false) {
    this.plan.statusMessages = [];
    if (apiPlan.statusmeldingen) {
      apiPlan.statusmeldingen.sort(this.sortApiStatusmeldingenOnColumn);
      const messages = apiPlan.statusmeldingen.map((msg) => ApiHelper.toStatusMessage(msg));
      if (messages?.length) {
        this.plan.statusMessages = messages.filter((msg) => this.messageHelper.isGenericStatusMessage(msg));
        if ((forValidation && !this.plan.statusMessages) || this.plan.statusMessages.length === 0) {
          // We need a plan-level message to ensure display of lower-level validation messages,
          // and to allow dismissal of the messages
          const validationMessage: StatusMessage = {
            code: 0,
            message: this.translate.instant("schedule.labelValidateFailed") as string,
            type: StatusMessageType.WARNING,
          };
          this.plan.statusMessages = [validationMessage];
        }
      } else if (forValidation) {
        // no validation messages
        const validationMessage: StatusMessage = {
          code: 0,
          message: this.translate.instant("schedule.labelValidateSuccess") as string,
          type: StatusMessageType.WARNING,
        };
        this.plan.statusMessages = [validationMessage];
      }

      this.enrichCoursesAndPeriodsWithStatusMessages(messages);
    }
  }

  private enrichCoursesAndPeriodsWithStatusMessages(messages: StatusMessage[]) {
    // Enrich courses and periods with status messages, or clean them up if there are none
    for (const studiejaar of this.plan.studyYears) {
      for (const period of studiejaar.periods) {
        period.statusMessages = messages.filter((msg) =>
          this.messageHelper.isPeriodStatusMessage(msg, studiejaar.yearNr, period.periodNr),
        );

        for (const course of period.courses) {
          course.statusMessages = messages.filter((msg) =>
            this.messageHelper.isCourseStatusMessage(msg, studiejaar.yearNr, period.periodNr, course.code),
          );
        }
      }
    }
  }

  private enrichRibbonCourses(year: StudyYear): void {
    const ribbonCourses: Course[] = [];

    year.periods.forEach((period) => {
      period.courses.forEach((course) => {
        if (this.courseHelper.hasRibbonPeriods(course)) {
          ribbonCourses.push(course);
        }
      });
    });

    ribbonCourses.forEach((course) => {
      this.courseHelper.planRibbonCourses(this.plan, course);
    });
  }

  private enrichCourses(id: number, year: StudyYear) {
    year.periods.forEach((period) => {
      period.courses.forEach((course: Course) => {
        course.planId = id;
        course.academicYear = year.academicYear;
        course.periodNr = period.periodNr;
        course.periodDescription = period.periodDescription;
      });

      period.courses = this.courseHelper.sortCourses(period.courses);
    });
  }

  /**
   * If a period includes info about tests that the student enrolled for,
   * enrich the courses in that period with the number of tests for each course.
   * If there are enrolled tests for which there is no course in the period,
   * add the test counts to the period object separately.
   * This should be done after enriching with ribbon courses,
   * so that test counts can be added to ribbon course repeats as well.
   */
  private enrichWithTests(year: StudyYear) {
    year.periods.forEach((period) => {
      this.planHelper.addCourseTests(this.plan, period);
    });
  }
}
